import React, { useState } from "react";
import { toast } from '../../../../components/common/Toast/toast';
import Modal from "../../components/Modal.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import { updateClassroom } from "../../../../../services/ClassroomService.js";
import { minutesToTimeLabel } from "../../classroomSchedule.js";
import TimeSummary from "./TimeSummary.jsx";

// A session row that names its batch is the bridge: click, Enter or Space.
const batchRowProps = (session, onOpenBatch) =>
  onOpenBatch && session.batchId
    ? {
        className: "pgm-row--open",
        tabIndex: 0,
        role: "button",
        "aria-label": `Open batch ${session.batchName || ""}`.trim(),
        onClick: () => onOpenBatch(session.batchId),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenBatch(session.batchId);
          }
        },
      }
    : {};

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/* `onSchedule(slot?)`, when given, is how a free day books straight into
   this lab: the footer's Schedule (the whole day) and each free slot (that
   slot). `onOpenBatch(batchId)`, when given, makes each session row the
   bridge to its batch. The page closes this popup before either hand-off.
   `onEditBooking(booking)`, when given, puts an Edit on each session row:
   the booking form opens over this popup, which stays up behind it. */
const RoomDetailModal = ({ room, branchName, canEdit, scheduleLabel, rangeNote, onClose, onChanged, onSchedule, onOpenBatch, onEditBooking }) => {
  const { classroom, status, sessions, freeWindows, summary } = room;
  const [reason, setReason] = useState(classroom.reason || "");
  const [saving, setSaving] = useState(false);
  const [showReasonBox, setShowReasonBox] = useState(false);

  const toggleMaintenance = async () => {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      await updateClassroom(classroom.id, {
        classroomNo: classroom.classroomNo,
        description: classroom.description,
        branch: classroom.branch,
        floor: classroom.floor,
        capacity: classroom.capacity,
        isActive: status === "maintenance",
        reason: status === "maintenance" ? "" : reason,
        slug: `/${slugify(classroom.classroomNo)}`,
        date: now,
        startTime: now,
        endTime: now,
        status: status === "maintenance" ? "active" : "inactive",
      });
      // The popup shows the done panel in place of its body and closes
      // itself after five seconds (Modal); the page reloads behind it.
      toast.success(status === "maintenance" ? "Marked back in service" : "Marked out of order");
      onChanged();
    } catch (error) {
      toast.error("Failed to update classroom status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      eyebrow="Lab / classroom"
      title={classroom.classroomNo}
      sub={`${branchName || classroom.branchName || "No branch"}${classroom.floor ? ` · ${classroom.floor}` : ""}`}
      onClose={onClose}
      size="wide"
      labelledBy="cls-room-title"
      footer={
        canEdit || (onSchedule && status !== "maintenance") ? (
          <>
            <div className="pgm-actions">
              {/* On ANY day this lab is usable, not only a wholly empty
                  one. One batch in the morning leaves the rest of the day
                  bookable, and `status === "free"` hid Schedule as soon as
                  a single session existed.

                  Out of order is the one exception, and it is not about
                  being busy: the room is broken, so nothing should be
                  booked into it. */}
              {onSchedule && status !== "maintenance" && (
                <button type="button" className="pgm-btn" onClick={() => onSchedule()}>
                  Schedule
                </button>
              )}
              {canEdit && status !== "maintenance" && !showReasonBox && (
                <button
                  type="button"
                  className="pgm-btn pgm-btn--danger"
                  onClick={() => setShowReasonBox(true)}
                >
                  Mark out of order
                </button>
              )}
              {canEdit && status === "maintenance" && (
                <button
                  type="button"
                  className="pgm-btn"
                  disabled={saving}
                  onClick={toggleMaintenance}
                >
                  {saving ? "Saving..." : "Mark back in service"}
                </button>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </>
        ) : (
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        )
      }
    >
      <dl className="quiz-facts">
        <div>
          <dt>Status</dt>
          <dd>
            {status === "occupied" && <StatusChip status="occupied">Occupied</StatusChip>}
            {status === "free" && <StatusChip status="free">Free</StatusChip>}
            {status === "maintenance" && <StatusChip status="maintenance">Out of order</StatusChip>}
          </dd>
        </div>
        <div>
          <dt>Branch</dt>
          <dd>{branchName || classroom.branchName || "\u2014"}</dd>
        </div>
        <div>
          <dt>Floor</dt>
          <dd>{classroom.floor || "\u2014"}</dd>
        </div>
        <div>
          <dt>Seats</dt>
          <dd>{classroom.capacity ?? "\u2014"}</dd>
        </div>
      </dl>

      {classroom.description && (
        <section className="pgm-section">
          <h5 className="pgm-section-title">Description</h5>
          <p className="lp-prose">{classroom.description}</p>
        </section>
      )}

      {status === "maintenance" && classroom.reason && (
        <p className="att-note att-note--warn">{classroom.reason}</p>
      )}

      <section className="pgm-section">
        <h5 className="pgm-section-title">{scheduleLabel || "Today's schedule"}</h5>

        {sessions.length ? (
          <div className="pgm-tablescroll">
            <table className="pgm-table">
              <thead>
                <tr>
                  <th className="pgm-num">#</th>
                  <th>Busy</th>
                  <th>Batch</th>
                  {onEditBooking && <th className="pgm-num">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sessions.map((session, i) => (
                  <tr key={session.batchId} {...batchRowProps(session, onOpenBatch)}>
                    <td className="pgm-num">{i + 1}</td>
                    <td>
                      {minutesToTimeLabel(session.startMin)} &ndash; {minutesToTimeLabel(session.endMin)}
                    </td>
                    <td>
                      {session.batchName}
                      {session.shifted ? " (shifted in)" : ""}
                    </td>
                    {/* The whole cell is kept off the row's open-the-batch
                        click -- that hand-off closes this popup, and a
                        click just beside Edit must not take it down. */}
                    {onEditBooking && (
                      <td
                        className="pgm-num"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {session.booking && (
                          <button
                            type="button"
                            className="pg-btn pg-btn--main"
                            onClick={() => onEditBooking(session.booking)}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="pgm-empty">No sessions scheduled that day.</p>
        )}

        {rangeNote && <p className="att-note">{rangeNote}</p>}
      </section>

      {/* The day as hours, above the bookable slots: this one SAYS how the
          day is spent, the one below ACTS on it. Kept apart rather than
          merged because a summary you can accidentally book into is worse
          than two short sections. */}
      {summary && status !== "maintenance" && (
        <TimeSummary
          summary={summary}
          sessions={sessions}
          freeWindows={freeWindows}
          dateLabel={scheduleLabel}
        />
      )}

      {freeWindows && (
        <section className="pgm-section">
          <h5 className="pgm-section-title">
            {onSchedule && status !== "maintenance" ? "Book a free slot" : "Free"}
          </h5>
          {/* Every gap, to the minute. Gaps under a quarter of an hour used
              to be dropped, so "free" and "busy" did not add up to the day. */}
          <div className="cls-legend">
            {freeWindows
              .filter((w) => w.endMin > w.startMin)
              .map((w) => {
                const label = (
                  <>
                    {minutesToTimeLabel(w.startMin)} &ndash; {minutesToTimeLabel(w.endMin)}
                  </>
                );
                // A slot on a bookable day schedules straight into it.
                return onSchedule && status !== "maintenance" ? (
                  <button
                    type="button"
                    key={`${w.startMin}-${w.endMin}`}
                    className="pg-pill cls-slot-btn"
                    onClick={() => onSchedule(w)}
                    title="Schedule in this slot"
                  >
                    {label}
                  </button>
                ) : (
                  <span key={`${w.startMin}-${w.endMin}`} className="pg-pill">
                    {label}
                  </span>
                );
              })}
          </div>
        </section>
      )}

      {/* Taking a room out of service asks for a reason first -- the
          field appears where the button was, and Confirm sits with it. */}
      {canEdit && status !== "maintenance" && showReasonBox && (
        <section className="pgm-section">
          <h5 className="pgm-section-title">Why is it out of order?</h5>
          <div className="pg-field">
            <label className="pg-label" htmlFor="cls-room-reason">
              Reason
            </label>
            <input
              id="cls-room-reason"
              className="pg-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Projector failure"
            />
          </div>
          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn"
              onClick={() => setShowReasonBox(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pg-btn pg-btn--main"
              disabled={saving}
              onClick={toggleMaintenance}
            >
              {saving ? "Saving..." : "Confirm"}
            </button>
          </div>
        </section>
      )}
    </Modal>
  );
};

export default RoomDetailModal;
